import { css, StyleSheet } from 'aphrodite';
import * as React from 'react';
import { Button } from '../../components-v2/Button';
import {
	Dropdown,
	DropdownCheckboxItem,
	DropdownContent,
	DropdownGroup,
	DropdownGroupLabel,
	DropdownItem,
	DropdownSelectTrigger,
} from '../../components-v2/Dropdown';
import { Pill } from '../../components-v2/Pill';
import {
	groupTriggerFilterOptions,
	type ITriggerFilterOption,
} from '../../containers/Automations/triggerFilterOptions';
import { THEME } from '../../components-v2/Theme/theme';
import { brandPrimary, brandPrimaryHover, grayIconFill, inputBorderColor, white } from '../../styles/colors';
import { SearchIcon } from '../svgs/icons/SearchIcon';
import { TextInput } from '../TextInput';

const ALL_TRIGGERS_LABEL = 'All Triggers';

interface ICreatorOption {
	title: string;
	value: string | null;
}

interface IStatusOption {
	label: string;
	value: string;
}

interface IAutomationFiltersProps {
	searchText: string;
	selectedCreatorId: string | null;
	creatorOptions: ICreatorOption[];
	onSearchTextChange: (text: string) => void;
	onCreatorFilterChange: (creatorId: string | null) => void;
	/** One list covering both trigger types and resource selectors */
	triggerOptions: ITriggerFilterOption[];
	/** Resolved rather than raw values, so a selection the options don't cover is still shown */
	selectedTriggerOptions: ITriggerFilterOption[];
	onTriggerFilterChange: (value: string, isSelected: boolean) => void;
	onClearTriggerFilter: () => void;
	/** Matching template count per option value, so empty combinations are visible up front */
	triggerOptionCounts?: Record<string, number>;
	// Optional status filter (for inactive tab)
	statusOptions?: IStatusOption[];
	selectedStatuses?: string[];
	onStatusChange?: (status: string) => void;
}

export const AutomationFilters: React.FC<IAutomationFiltersProps> = ({
	searchText,
	selectedCreatorId,
	creatorOptions,
	onSearchTextChange,
	onCreatorFilterChange,
	triggerOptions,
	selectedTriggerOptions,
	onTriggerFilterChange,
	onClearTriggerFilter,
	triggerOptionCounts,
	statusOptions,
	selectedStatuses,
	onStatusChange,
}) => {
	const selectedCreatorOption = creatorOptions.find(option => option.value === selectedCreatorId) || creatorOptions[0];
	const groupedTriggerOptions = React.useMemo(() => groupTriggerFilterOptions(triggerOptions), [triggerOptions]);
	const selectedTriggerValues = React.useMemo(
		() => selectedTriggerOptions.map(option => option.value),
		[selectedTriggerOptions]
	);

	const statusLabel = React.useMemo(() => {
		if (!statusOptions || !selectedStatuses) return '';
		if (selectedStatuses.length === statusOptions.length) return 'All Statuses';
		return selectedStatuses.map(s => statusOptions.find(o => o.value === s)?.label).join(', ');
	}, [statusOptions, selectedStatuses]);

	// A count keeps the trigger a fixed width no matter how many options are checked; the
	// pills below spell out the actual selection
	const triggerLabel =
		selectedTriggerOptions.length === 0
			? ALL_TRIGGERS_LABEL
			: selectedTriggerOptions.length === 1
				? selectedTriggerOptions[0].label
				: `${selectedTriggerOptions.length} triggers`;

	return (
		<div className={css(styleSheet.container)}>
			<div className={css(styleSheet.controlsRow)}>
				<div className={css(styleSheet.searchContainer)}>
					<TextInput
						type='text'
						inputId='search-automations'
						placeholder='Search by automation name...'
						value={searchText}
						onChange={e => onSearchTextChange(e.target.value)}
						leftAccessory={<SearchIcon className={css(styleSheet.searchIcon)} fillColor={grayIconFill} />}
						rightAccessory={
							searchText ? (
								<button type='button' className={css(styleSheet.clearButton)} onClick={() => onSearchTextChange('')}>
									Clear
								</button>
							) : null
						}
						className={css(styleSheet.searchInputWrapper)}
						inputClassName={css(styleSheet.searchInput)}
					/>
				</div>
				<div className={css(styleSheet.triggerFilterContainer)}>
					<Dropdown>
						<DropdownSelectTrigger styles={[styleSheet.dropdownTrigger]} title={triggerLabel}>
							<span className={css(styleSheet.truncatedTriggerLabel)}>{triggerLabel}</span>
						</DropdownSelectTrigger>
						<DropdownContent styles={[styleSheet.dropdownContent]}>
							{groupedTriggerOptions.map(({ group, options }) => (
								<DropdownGroup key={group}>
									<DropdownGroupLabel>{group}</DropdownGroupLabel>
									{options.map(option => (
										<DropdownCheckboxItem
											key={option.value}
											checked={selectedTriggerValues.includes(option.value)}
											onCheckedChange={isSelected => onTriggerFilterChange(option.value, isSelected)}
											closeOnClick={false}
											styles={[styleSheet.dropdownItem]}
										>
											<span className={css(styleSheet.optionRow)}>
												<span>{option.label}</span>
												{triggerOptionCounts ? (
													<span className={css(styleSheet.optionCount)}>{triggerOptionCounts[option.value] ?? 0}</span>
												) : null}
											</span>
										</DropdownCheckboxItem>
									))}
								</DropdownGroup>
							))}
						</DropdownContent>
					</Dropdown>
				</div>
				<div className={css(styleSheet.creatorFilterContainer)}>
					<Dropdown>
						<DropdownSelectTrigger styles={[styleSheet.dropdownTrigger]}>
							{selectedCreatorOption?.title || 'Show all creators'}
						</DropdownSelectTrigger>
						<DropdownContent styles={[styleSheet.dropdownContent]}>
							{creatorOptions.map(option => (
								<DropdownItem
									key={option.value || 'all'}
									onClick={() => onCreatorFilterChange(option.value)}
									styles={[styleSheet.dropdownItem]}
								>
									{option.title}
								</DropdownItem>
							))}
						</DropdownContent>
					</Dropdown>
				</div>
				{statusOptions && selectedStatuses && onStatusChange && (
					<div className={css(styleSheet.statusFilterContainer)}>
						<Dropdown>
							<DropdownSelectTrigger styles={[styleSheet.dropdownTrigger]} title={statusLabel}>
								<span className={css(styleSheet.truncatedTriggerLabel)}>{statusLabel}</span>
							</DropdownSelectTrigger>
							<DropdownContent styles={[styleSheet.dropdownContent]}>
								{statusOptions.map(option => (
									<DropdownCheckboxItem
										key={option.value}
										checked={selectedStatuses.includes(option.value)}
										onCheckedChange={() => onStatusChange(option.value)}
										closeOnClick={false}
										styles={[styleSheet.dropdownItem]}
									>
										{option.label}
									</DropdownCheckboxItem>
								))}
							</DropdownContent>
						</Dropdown>
					</div>
				)}
			</div>
			{selectedTriggerOptions.length > 0 && (
				<div className={css(styleSheet.selectionRow)}>
					{selectedTriggerOptions.map(option => (
						<Pill
							key={option.value}
							variant='keyword'
							label={option.label}
							value={option.value}
							onRemove={value => onTriggerFilterChange(value, false)}
						/>
					))}
					<Button variant='link' size='sm' onClick={onClearTriggerFilter}>
						Clear all
					</Button>
				</div>
			)}
		</div>
	);
};

const styleSheet = StyleSheet.create({
	container: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.sm,
		alignItems: 'flex-start',
	},
	controlsRow: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: THEME.spacing.md,
		alignItems: 'center',
		justifyContent: 'flex-start',
	},
	selectionRow: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: THEME.spacing.sm,
		alignItems: 'center',
	},
	searchContainer: {
		flex: '0 0 300px',
		width: '300px',
	},
	searchInputWrapper: {
		background: white,
		borderRadius: '6px',
		border: `1px solid ${inputBorderColor}`,
		height: '40px',
		display: 'flex',
		alignItems: 'center',
		boxSizing: 'border-box',
		':hover': {
			borderColor: brandPrimary,
		},
	},
	searchInput: {
		paddingLeft: 8,
	},
	searchIcon: {
		width: '20px !important',
		height: '20px !important',
		marginRight: 8,
		flexShrink: 0,
	},
	clearButton: {
		background: 'none',
		border: 'none',
		color: brandPrimary,
		cursor: 'pointer',
		fontSize: '14px',
		fontWeight: 500,
		padding: '4px 12px 4px 8px',
		borderRadius: '4px',
		':hover': {
			fontWeight: 700,
		},
	},
	triggerFilterContainer: {
		flex: '0 0 auto',
		width: 220,
	},
	creatorFilterContainer: {
		flex: '0 0 auto',
		minWidth: '200px',
	},
	statusFilterContainer: {
		flex: '0 0 auto',
		width: 220,
	},
	// Caps the label inside the 220px trigger, leaving room for padding and the chevron
	truncatedTriggerLabel: {
		display: 'block',
		maxWidth: 170,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	dropdownTrigger: {
		height: '40px',
		width: '100%',
		borderRadius: '6px',
		border: `1px solid ${inputBorderColor}`,
		backgroundColor: white,
		padding: '12px',
		fontSize: '14px',
		color: '#4A4A4A',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		':hover': {
			borderColor: brandPrimary,
		},
	},
	dropdownContent: {
		maxHeight: '300px',
		overflowY: 'auto',
		overflowX: 'hidden',
		minWidth: 260,
	},
	dropdownItem: {
		padding: '8px 12px',
		fontSize: '14px',
		color: '#4A4A4A',
		cursor: 'pointer',
		':hover': {
			backgroundColor: brandPrimaryHover,
			color: white,
		},
	},
	optionRow: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: THEME.spacing.sm,
		width: '100%',
	},
	optionCount: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.xs,
	},
});
